import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { brand } from '../config/brand';

interface LocalStorageState {
  addEndLogo: boolean;
  debugMode: boolean; // If true, simulate upload without actually publishing
  uploadAccount1: boolean;
  uploadAccount2: boolean;
  selectedPlatforms: string[]; // Array of selected platform IDs: 'instagram', 'tiktok', 'threads', 'x', 'youtube', 'bluesky', 'pinterest'
  showStoryOverlay: boolean; // If true, show mobile/story format overlay
}

interface LevelInfo {
  level: number;
  currentXp: number;
  nextLevelXp: number;
  progressPercent: number;
  badgeColor: string;
}

interface User {
  id: string;
  email: string;
  name?: string;
  isAdmin?: boolean;
  isEmailVerified?: boolean;
  autoReplyUsername?: boolean;
  youtubeFallback?: boolean;
  unifyFormat?: boolean;
  evergreenTags?: string[];
  tiktokDefaults?: {
    privacyLevel: string;
    allowComments: boolean;
    allowDuet: boolean;
    allowStitch: boolean;
    contentDisclosure: boolean;
    yourBrand: boolean;
    brandedContent: boolean;
  };
  youtubeDefaults?: {
    videoType: 'video' | 'short';
  };
  tier?: 'FREE' | 'BASIC' | 'PRO' | 'AGENCY';
  levelInfo?: LevelInfo;
  totalExperience?: number;
  maxStreak?: number;
  totalMissionsCompleted?: number;
  totalMissionsCompletedToday?: number;
  totalPosts?: number;
  featureFlags?: Record<string, boolean>;
  polarCustomerId?: string;
  polarSubscriptionId?: string;
  subscriptionStatus?: string;
  subscriptionEndsAt?: string;
  isManualSubscription?: boolean;
  usedFreeMonthOffer?: boolean;
  purgeOnSubscriptionExpiry?: boolean;
  hasPassword?: boolean;
  googleId?: string;
}

interface GlobalState {
  localStorage: LocalStorageState;
  updateLocalStorage: (updates: Partial<LocalStorageState>) => void;
  isServerOffline: boolean;
  setIsServerOffline: (offline: boolean) => void;
  user: User | null;
  setUser: (user: User | null) => void;
  refreshUser: () => Promise<void>;
}

const defaultState: LocalStorageState = {
  addEndLogo: false,
  debugMode: false, // Default: don't use debug mode
  uploadAccount1: true,
  uploadAccount2: false,
  selectedPlatforms: [], // Default: no platforms selected
  showStoryOverlay: true, // Default: show story overlay
};

const STORAGE_KEY = brand.stateStorageKey;

const GlobalStateContext = createContext<GlobalState | undefined>(undefined);

export function GlobalStateProvider({ children }: { children: ReactNode }) {
  const [isServerOffline, setIsServerOffline] = useState(false);
  const [user, setUserState] = useState<any | null>(() => {
    try {
      const stored = localStorage.getItem('user');
      if (!stored) return null;
      const parsed = JSON.parse(stored);

      // Safety check for evergreenTags in stored user
      if (parsed && parsed.evergreenTags && typeof parsed.evergreenTags === 'string') {
        try {
          parsed.evergreenTags = JSON.parse(parsed.evergreenTags);
        } catch (e) {
          parsed.evergreenTags = [];
        }
      }
      return parsed;
    } catch (e) {
      return null;
    }
  });

  const setUser = useCallback((newUser: any | null) => {
    // Safety check for evergreenTags - ensure it's always an array
    if (newUser && newUser.evergreenTags && typeof newUser.evergreenTags === 'string') {
      try {
        newUser.evergreenTags = JSON.parse(newUser.evergreenTags);
      } catch (e) {
        newUser.evergreenTags = [];
      }
    }

    setUserState(newUser);
    if (newUser) {
      localStorage.setItem('user', JSON.stringify(newUser));
    } else {
      localStorage.removeItem('user');
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.user) {
        setUser(response.data.user);
      }
    } catch (e) {
      console.error('Error refreshing user data:', e);
    }
  }, [setUser]);

  const [localStorageState, setLocalStorageState] = useState<LocalStorageState>(() => {
    // Load from localStorage on mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to handle missing properties
        return { ...defaultState, ...parsed };
      }
    } catch (error) {
      console.error('Error loading global state from localStorage:', error);
    }
    return defaultState;
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(localStorageState));
    } catch (error) {
      console.error('Error saving global state to localStorage:', error);
    }
  }, [localStorageState]);

  const updateLocalStorage = useCallback((updates: Partial<LocalStorageState>) => {
    setLocalStorageState(prev => ({ ...prev, ...updates }));
  }, []);

  const value: GlobalState = {
    localStorage: localStorageState,
    updateLocalStorage,
    isServerOffline,
    setIsServerOffline,
    user,
    setUser,
    refreshUser,
  };

  return (
    <GlobalStateContext.Provider value={value}>
      {children}
    </GlobalStateContext.Provider>
  );
}

export function useGlobalState(): GlobalState {
  const context = useContext(GlobalStateContext);
  if (context === undefined) {
    throw new Error('useGlobalState must be used within a GlobalStateProvider');
  }
  return context;
}
